import { Controller } from "@hotwired/stimulus";

/**
* This controller is used to create dropdowns.
* You can choose between two events to open the dropdown,
* with a click or mouseover.
* You can nest as many dropdowns as you want. In
* 'app/views/sandbox/_nav-menu.html.erb' you can view
* an example that contains nested dropdowns.
*
* @markup-example
* @event -> mouseeover:
*
* <div class="relative"
*    data-controller="dropdown-menu"
*    data-action="mouseenter->dropdown-menu#showDropdown mouseleave->dropdown-menu#hideDropdown"
* >
* <button
*   data-action="mouseenter->dropdown-menu#showDropdown",
*   data-dropdown-menu-target="toggleButton"/>
*   BUTTON
* </button>
* <div
*   class="dropdown-menu dropdown-container top-0 shadow-xl rounded-sm opacity-0 hidden"
*   data-dropdown-menu-target="toggleContainer"
*   data-action="mouseenter->dropdown-menu#showDropdown"
* >
* </div>
*
* @markup-example
* @event -> click:
*
* <div class="relative"
*    data-controller="dropdown-menu"
*    data-action="click@window->dropdown-menu#checkTargetDropdown"
* >
* <button
*   data-action="click->dropdown-menu#showDropdown",
*   data-dropdown-menu-target="toggleButton"/>
*   BUTTON
* </button>
* <div
*   class="dropdown-menu absolute right-0 top-0 shadow-xl rounded-sm opacity-0 hidden"
*   data-dropdown-menu-target="toggleContainer"
* >
* </div>
*
*
* Keyboard support is wired up automatically — no markup changes are needed at
* the call sites, including the ~40 dropdowns whose trigger is a plain
* <div>/<span> (the controller makes those focusable itself). Mouse behaviour
* (and therefore the visual design) is untouched; the keyboard path only adds
* what was previously unreachable:
*
* - Hover-driven dropdowns also open when the trigger receives focus, mirroring
*   what `mouseenter` does for pointer users.
* - ArrowDown / ArrowUp on the trigger open the menu and move focus to its
*   first / last item. Once inside, the arrows walk the items and Home / End
*   jump to either end.
* - Escape closes the menu and returns focus to the trigger.
* - Triggers that are anchors with no real destination (`href=""` or `"#"`,
*   used purely to open the menu) also open on Enter / Space, which previously
*   just reloaded the page.
* - `aria-expanded` is kept in sync on the trigger so screen readers announce
*   the menu's state.
*
* The one thing a call site still owns: menu items have to be real links or
* buttons, since that is what the arrow keys move between.
*/

// Elements that can receive focus inside an open dropdown, in DOM order.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",");

// Subtrees that are not rendered: the `hidden` attribute, and the `hidden`
// utility class this very controller uses to collapse containers.
const INVISIBLE_ANCESTOR_SELECTOR = "[hidden],.hidden";

// `aria-expanded` is only valid on elements whose role supports it; on a bare
// <div> it is an ARIA violation, not an improvement.
const NATIVE_EXPANDABLE_SELECTOR = "a[href],button,summary";
const EXPANDABLE_ROLE_SELECTOR = '[role="button"],[role="combobox"],[role="link"],[role="menuitem"]';

// Controls that consume the arrow keys themselves: a <select> walks its
// options, a textarea moves the caret between lines.
const OWNS_ARROWS_SELECTOR = 'select,textarea,[contenteditable="true"]';

// Text entry, where Home / End belong to the caret. ArrowUp/Down do nothing in
// a single-line field, so there they keep walking the menu — the usual
// filter-field-plus-list behaviour of our dropdowns.
const TEXT_ENTRY_SELECTOR = [
  "input:not([type=checkbox]):not([type=radio]):not([type=submit]):not([type=button])",
  "textarea",
  '[contenteditable="true"]'
].join(",");

const HOVER_ACTION = "mouseenter->dropdown-menu#showDropdown";

export default class extends Controller {
  static targets = ["toggleButton", "toggleContainer"]
  static values = {
    turboLoadUrl: String,
    useFixedPosition: {
      type: Boolean,
      default: false
    },
    alreadyTurboLoaded: {
      type: Boolean,
      default: false
    },
    openingDelay: {
      type: Number,
      default: 150
    },
    closingDelay: {
      type: Number,
      default: 150
    },
    visible: {
      type: String,
      default: "open active"
    },
    invisible: {
      type: String,
      default: "hidden close opacity-0"
    }
  }

  // Targets connect before the controller does, and they already need to know
  // which classes mean "open", so this cannot wait for connect().
  initialize() {
    // CSS Classes would be more appropiate, https://stimulus.hotwired.dev/reference/css-classes
    // but they don't provide "default" settings, https://github.com/hotwired/stimulus/issues/458
    // However, for Values (https://stimulus.hotwired.dev/reference/values#default-values) it's required to do this:
    const allInvisible = this.invisibleValue.split(" ")
    this.VISIBLE_CLASSES = this.visibleValue.split(" ")
    this._usesHidden = allInvisible.includes("hidden")
    this.INVISIBLE_CLASSES = allInvisible.filter(c => c !== "hidden")
  }

  connect() {
    // Only hover-driven dropdowns open on focus; click-driven ones must keep
    // waiting for an explicit activation.
    this._hoverDriven = this._declaredActions().some(action => action.includes(HOVER_ACTION))

    this._boundKeydown = this._handleKeydown.bind(this)
    this._boundFocusIn = this._handleFocusIn.bind(this)
    this._boundFocusOut = this._handleFocusOut.bind(this)
    this.element.addEventListener("keydown", this._boundKeydown)
    this.element.addEventListener("focusin", this._boundFocusIn)
    this.element.addEventListener("focusout", this._boundFocusOut)

    this._prepareTrigger()
    this._setExpanded(this._isOpen())
  }

  disconnect() {
    this.element.removeEventListener("keydown", this._boundKeydown)
    this.element.removeEventListener("focusin", this._boundFocusIn)
    this.element.removeEventListener("focusout", this._boundFocusOut)

    this._cancelPendingTimers()
  }

  toggleContainerTargetConnected(element) {
    if (element.classList.contains("open")) {
      this._isContentOffViewport()
      this._setExpanded(this._isOpen())
    }
  }

  showDropdown(event) {
    event.preventDefault();

    this._suppressFocusOpen = false

    if (this._hiddenTimeout) {
      clearTimeout(this._hiddenTimeout);
      this._hiddenTimeout = null;
    }

    // Run the callback immediately when the event is clicked,
    // otherwise, set a timeout
    if (event.type === "click") {
      return this._reveal()
    }

    // Cancel multiple showDropdown events
    if (this.timeout) { clearTimeout(this.timeout); }

    // Postpone the event
    this.timeout = setTimeout(() => this._reveal(), this.openingDelayValue);
  }

  // TODO:
  // remove this method, it creates too much listeners everywhere
  // The way to do this is about setting a listener (to the document)
  // ONLY when the dropdown is shown, and remove this listener when is hidden
  checkTargetDropdown({ target }) {
    if (!this.hasToggleContainerTarget) return

    if (
      !this.toggleContainerTarget.contains(target) &&
      this.toggleContainerTarget.classList.contains("active") &&
      !target.classList.contains("js-skip-dropdown-trigger") &&
      target.type !== "submit"
    ) {
      this.hideDropdown();
    }
  }

  hideDropdown() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    // Set the same timeout for closing the element, consecutive events will be cancelled
    this.timeout = setTimeout(() => {
      if (!this.hasToggleContainerTarget) return

      this._applyInvisibleClasses()

      if (this._usesHidden) {
        const el = this.toggleContainerTarget;
        const durations = getComputedStyle(el).transitionDuration.split(",");
        const maxMs = Math.max(0, ...durations.map(d => parseFloat(d) * 1000));

        this._hiddenTimeout = setTimeout(() => {
          el.classList.add("hidden");
          this._hiddenTimeout = null;
        }, maxMs);
      }
    }, this.closingDelayValue);
  }

  // --- Keyboard support -----------------------------------------------------

  _handleKeydown(event) {
    // Something closer to the user already acted on this key: a nested
    // dropdown, an inner autocomplete, the editor.
    if (event.defaultPrevented) return

    if (event.key === "Escape") {
      if (!this._isOpen()) return

      this._consume(event)
      this._hideImmediately()
      this._suppressFocusOpen = true
      this._focusTrigger()
      return
    }

    const onTrigger = this.hasToggleButtonTarget &&
      (this.toggleButtonTarget === event.target || this.toggleButtonTarget.contains(event.target))
    const inMenu = this.hasToggleContainerTarget && this.toggleContainerTarget.contains(event.target)

    if (!onTrigger && !inMenu) return

    if (onTrigger) {
      // Triggers that have no activation behaviour of their own need it here,
      // otherwise Enter either does nothing or reloads the current page. This
      // looks at the focused element, not at the target: a trigger can wrap a
      // real link, and that link has to keep navigating.
      if ((event.key === "Enter" || event.key === " ") && this._lacksOwnActivation(event.target)) {
        this._consume(event)
        this._openAndFocus(0)
        return
      }

      if (event.key === "ArrowDown") {
        this._consume(event)
        this._openAndFocus(0)
        return
      }

      if (event.key === "ArrowUp") {
        this._consume(event)
        this._openAndFocus(-1)
        return
      }

      return
    }

    const items = this._focusableItems()
    if (items.length === 0) return
    if (this._targetOwnsKey(event.target, event.key)) return

    const currentIndex = items.indexOf(event.target)

    switch (event.key) {
      case "ArrowDown":
        this._consume(event)
        this._focusItem(items, currentIndex + 1)
        break
      case "ArrowUp":
        this._consume(event)
        this._focusItem(items, currentIndex - 1)
        break
      case "Home":
        this._consume(event)
        this._focusItem(items, 0)
        break
      case "End":
        this._consume(event)
        this._focusItem(items, items.length - 1)
        break
    }
  }

  // A key we act on must not reach the enclosing dropdown: nested menus would
  // otherwise move focus twice and close every level at once.
  _consume(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  // Keys the focused control needs for itself win over menu navigation.
  _targetOwnsKey(element, key) {
    if (element.matches(OWNS_ARROWS_SELECTOR)) return true

    return (key === "Home" || key === "End") && element.matches(TEXT_ENTRY_SELECTOR)
  }

  _handleFocusIn(event) {
    if (!this._hoverDriven) return
    if (!this.hasToggleButtonTarget) return
    if (this.toggleButtonTarget !== event.target && !this.toggleButtonTarget.contains(event.target)) return

    // Escape returns focus to the trigger, which would otherwise reopen the
    // menu we were just asked to dismiss. It stays closed until focus leaves
    // and comes back, or the user opens it explicitly.
    if (this._suppressFocusOpen) {
      this._suppressFocusOpen = false
      return
    }

    if (this._hiddenTimeout) {
      clearTimeout(this._hiddenTimeout)
      this._hiddenTimeout = null
    }
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }

    this._reveal({ immediate: true })
  }

  _handleFocusOut(event) {
    // A null relatedTarget means focus went nowhere focusable — typically a
    // click on a non-interactive area. Closing there would break the pointer
    // behaviour, so only react when focus actually moved somewhere outside.
    // The same click lands on a focusable ancestor such as main[tabindex=-1].
    const next = event.relatedTarget
    if (!next || this.element.contains(next) || next.contains(this.element)) return

    this._suppressFocusOpen = false
    if (!this._isOpen()) return

    this.hideDropdown()
  }

  _openAndFocus(index) {
    this._reveal({ immediate: true }).then(() => {
      const items = this._focusableItems()
      if (items.length === 0) return

      this._focusItem(items, index)
    })
  }

  // Negative indexes count from the end, so -1 is the last item.
  _focusItem(items, index) {
    const wrapped = (index + items.length) % items.length
    items[wrapped]?.focus()
  }

  _focusableItems() {
    if (!this.hasToggleContainerTarget) return []

    return Array.from(this.toggleContainerTarget.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(el => !el.closest(INVISIBLE_ANCESTOR_SELECTOR) && el.getAttribute("aria-hidden") !== "true")
  }

  _focusTrigger() {
    this._triggerElement()?.focus()
  }

  // Plain <div>/<span> triggers are unreachable by keyboard and cannot carry
  // aria-expanded without a role. Both belong to the widget, not to the ~40
  // call sites that would otherwise have to be edited one by one. An explicit
  // tabindex is left alone: the author already decided.
  _prepareTrigger() {
    if (!this.hasToggleButtonTarget) return

    const trigger = this.toggleButtonTarget
    if (trigger.hasAttribute("tabindex")) return
    if (trigger.matches(FOCUSABLE_SELECTOR)) return
    // A trigger that wraps its own control already has a keyboard entry point,
    // and nesting a role="button" around it would only hide that control.
    if (trigger.querySelector(FOCUSABLE_SELECTOR)) return

    trigger.setAttribute("tabindex", "0")
    if (!trigger.hasAttribute("role")) trigger.setAttribute("role", "button")
  }

  // The element the user focuses to operate the menu: the trigger itself once
  // it can take focus, or the control it wraps.
  _triggerElement() {
    if (!this.hasToggleButtonTarget) return null

    const trigger = this.toggleButtonTarget
    if (trigger.matches(FOCUSABLE_SELECTOR)) return trigger

    return trigger.querySelector(FOCUSABLE_SELECTOR) || trigger
  }

  // True when the focused element does nothing on its own when activated: an
  // anchor with no destination (`link_to ''` / `link_to '#'`, kept around
  // purely to open the dropdown), or an element that isn't natively
  // interactive at all. A real <button> or form control is left alone.
  _lacksOwnActivation(element) {
    if (element.tagName === "A") {
      const href = element.getAttribute("href")
      return href === null || href === "" || href === "#"
    }

    return !["BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(element.tagName)
  }

  _isOpen() {
    if (!this.hasToggleContainerTarget) return false
    // Many call sites render the container with both `open` and `hidden`: it is
    // `hidden` that decides, or we would report a collapsed menu as expanded
    // and swallow the Escape that belongs to whatever encloses it.
    if (this._usesHidden && this.toggleContainerTarget.classList.contains("hidden")) return false

    return this.VISIBLE_CLASSES.some(klass => this.toggleContainerTarget.classList.contains(klass))
  }

  _setExpanded(expanded) {
    // Nothing to expand yet: the menu of a turbo-loaded dropdown arrives later.
    if (!this.hasToggleContainerTarget) return

    const trigger = this._triggerElement()
    if (!trigger || !this._canExpressExpanded(trigger)) return

    trigger.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  // An explicit role replaces the native semantics, so it decides on its own:
  // our microtip tooltips are real <button role="tooltip">, and aria-expanded
  // is invalid there.
  _canExpressExpanded(element) {
    if (element.hasAttribute("role")) return element.matches(EXPANDABLE_ROLE_SELECTOR)

    return element.matches(NATIVE_EXPANDABLE_SELECTOR)
  }

  // Shared with hideDropdown; there the transition runs first and `hidden`
  // lands afterwards, here everything happens at once.
  _applyInvisibleClasses() {
    this.toggleContainerTarget.classList.remove(...this.VISIBLE_CLASSES)
    this.toggleContainerTarget.classList.add(...this.INVISIBLE_CLASSES)
    this._setExpanded(false)
  }

  _hideImmediately() {
    this._cancelPendingTimers()
    this._applyInvisibleClasses()
    if (this._usesHidden) this.toggleContainerTarget.classList.add("hidden")
  }

  // A pending open or close would land after the fact: the keyboard closes now,
  // and a disconnected controller must not touch the DOM at all.
  _cancelPendingTimers() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
    if (this._hiddenTimeout) {
      clearTimeout(this._hiddenTimeout)
      this._hiddenTimeout = null
    }
  }

  // --- Showing --------------------------------------------------------------

  // Reveals the container. Pointer-driven opens keep the original double-rAF so
  // CSS transitions interpolate from the painted invisible state; keyboard
  // opens skip it, because focus has to land on a real item in the same tick.
  _reveal({ immediate = false } = {}) {
    if (this.hasTurboLoadUrlValue && !this.alreadyTurboLoadedValue) {
      this._turboLoadContainer();
    }

    if (!this.hasToggleContainerTarget) return Promise.resolve()

    const applyVisibleClasses = () => {
      this.toggleContainerTarget.classList.remove(...this.INVISIBLE_CLASSES);
      this.toggleContainerTarget.classList.add(...this.VISIBLE_CLASSES);
      this._setExpanded(true)

      if (this.useFixedPositionValue) {
        this._positionFixed();
      } else {
        this._isContentOffViewport();
      }
    }

    if (immediate) {
      if (this._usesHidden) {
        this.toggleContainerTarget.classList.remove("hidden");
      }
      applyVisibleClasses()
      return Promise.resolve()
    }

    // Run asynchronously so checkTargetDropdown sees the container
    // without "active" during the same event loop tick.
    // Double-rAF: frame 1 removes display:none so the browser paints
    // the initial (invisible) state; frame 2 toggles classes, letting
    // CSS transitions interpolate from that painted state.
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        if (this._usesHidden) {
          this.toggleContainerTarget.classList.remove("hidden");
        }

        requestAnimationFrame(() => {
          applyVisibleClasses()
          resolve()
        });
      });
    })
  }

  _declaredActions() {
    const sources = [this.element.getAttribute("data-action")]
    if (this.hasToggleButtonTarget) {
      sources.push(this.toggleButtonTarget.getAttribute("data-action"))
    }

    return sources.filter(Boolean)
  }

  _positionFixed() {
    const rect = this.toggleButtonTarget.getBoundingClientRect();
    this.toggleContainerTarget.style.position = "fixed";
    this.toggleContainerTarget.style.top = `${rect.bottom + 4}px`;
    this.toggleContainerTarget.style.left = `${rect.left}px`;
  }

  _isContentOffViewport() {
    const toggleContainerOffsets = this.toggleContainerTarget.getBoundingClientRect();

    if(toggleContainerOffsets.width + toggleContainerOffsets.left > document.documentElement.clientWidth) {
      this.toggleContainerTarget.classList.add("dropdown-container-right");
    } else {
      this.toggleContainerTarget.classList.add("dropdown-container-left");
    }

    if(toggleContainerOffsets.height + toggleContainerOffsets.y > window.innerHeight) {
      this.toggleContainerTarget.classList.add("scroll-mb-6");
      this.toggleContainerTarget.scrollIntoView({
        block: "end",
        behavior: "smooth"
      })
    }
  }

  _turboLoadContainer(){
    fetch(this.turboLoadUrlValue, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
    .then(response => response.text())
    .then(html => Turbo.renderStreamMessage(html))

    this.alreadyTurboLoadedValue = true;
  }
}
